import { Menu } from "@mui/icons-material"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import { IconButton, Stack, Typography } from "@mui/material"
import { useEffect, useState } from "react"

import Button from "@components/button"
import PageHeader from "@components/page_header"
import { PRIMARY } from "@constants/colors"

import DeleteWhatsappApi from "@components/dialogs/delete_whatsapp_api"
import {
  WhatsappFormData,
  useWhatsappPageContext
} from "@contexts/main/whatsapp_page_context"
import { updatePlatforms } from "@domain/whatsappApi"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { PlatformType, RoleTypes } from "__generated__/graphql"
import { useNavigate, useParams } from "react-router-dom"
import styles from "./styles"

export default function WhatsappBuilderPageHeader() {
  const {
    me,
    translate,
    isLeftDrawerOpen,
    toggleIsLeftDrawerOpen,
    handleSubmit,
    whatsappData,
    isValid,
    isDirty
  } = useWhatsappPageContext()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState<boolean>(false)
  const [customerId, setCustomerId] = useState<string>()
  const navigate = useNavigate()

  function backToAllStatusClick() {
    navigate("/whatsapp")
  }

  const { id } = useParams()
  const selectedId = id

  async function onSubmit(data: WhatsappFormData) {
    if (!selectedId || !customerId) {
      return
    }
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await updatePlatforms({
      metaData: {
        _id: selectedId,
        customerId: customerId,
        name: data.displayName,
        status: data.status ? true : false,
        appName: data.appName,
        appId: data.appID,
        appToken: data.appToken,
        apiKey: data.appKey,
        product_template_id: data.productTemplate
      },
      type: PlatformType.WhatsAppBusiness
    })
    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    queryClient.invalidateQueries({ queryKey: ["whatsapp-data-table"] })
    setLoading(false)
  }

  useEffect(() => {
    const customer = whatsappData?.find((item) => item._id === selectedId)
      ?.customer._id
    setCustomerId(customer)
  }, [whatsappData, selectedId])

  function handleDelete() {
    if (!selectedId) {
      return
    }
    setOpenDelete(true)
  }

  return (
    <PageHeader sx={styles.content} isLeftPanelOpen={isLeftDrawerOpen}>
      <Stack direction={"row"} alignItems={"center"} columnGap={1} pl={1}>
        <IconButton
          onClick={toggleIsLeftDrawerOpen}
          disabled={!!isLeftDrawerOpen}
          disableRipple
          sx={{
            display: !!isLeftDrawerOpen ? "none" : "block",
            pl: 0,
            ":hover": {
              color: PRIMARY
            }
          }}
        >
          <Menu />
        </IconButton>
        <IconButton
          onClick={() => backToAllStatusClick()}
          disableRipple
          sx={{
            pl: 0,
            ":hover": {
              color: PRIMARY
            }
          }}
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6">Whatsapp API</Typography>
      </Stack>
      <Stack flexDirection={"row"} gap={1}>
        <Button
          disabled={me?.role === RoleTypes.User}
          onClick={handleDelete}
          color="error"
        >
          {translate("delete")}
        </Button>
        <Button
          loading={loading}
          disabled={!isValid || !isDirty}
          onClick={handleSubmit?.(onSubmit)}
        >
          {translate("save")}
        </Button>
      </Stack>
      <DeleteWhatsappApi
        isOpen={openDelete}
        selectedRow={selectedId}
        customerId={customerId}
        setClose={() => setOpenDelete(false)}
      />
    </PageHeader>
  )
}
